import { useTranslation } from 'react-i18next';

import { borderColor } from '@/styles/variants';
import { Tooltip } from '@arco-design/web-react';
import { IconDown, IconFile, IconRight } from '@arco-design/web-react/icon';
import { createPatch } from 'diff';
import { html, parse } from 'diff2html';
import 'diff2html/bundles/css/diff2html.min.css';
import React, { useEffect, useState } from 'react';
import isEqual from 'react-fast-compare';
import styled from 'styled-components';

type DiffMode = 'line-by-line' | 'side-by-side';

interface DiffYamlProps {
  /** 标题 - default = "配置文件" */
  title?: string;
  /** 描述 - default = undefined */
  description?: string;
  /** 对比数据 */
  datas?: [string, string];
  /** 容器样式 */
  styles?: React.CSSProperties;
}

function DiffYamlComponent({ title, description, datas = ['', ''], styles }: DiffYamlProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<DiffMode>('line-by-line');
  const [diffHtml, setDiffHtml] = useState<string>('');

  const getDiffHtml = ([oldData, newData]: [string, string], options = {}) => {
    /**
     * diff2html 需要一个符合 unified diff 格式标准的补丁字符串才能正确解析和渲染出差异内容。
     * 可以查新类型定义以确认传参的内容含义
     */
    const diffStr = createPatch('yaml', oldData, newData, '', '', {});
    const diffJson = parse(diffStr);
    return html(diffJson, {
      drawFileList: false,
      ...options,
    });
  };

  useEffect(() => {
    setDiffHtml(
      getDiffHtml(datas, {
        outputFormat: mode,
      }),
    );
  }, [datas, mode]);

  const handleModeChange = (newMode: DiffMode) => () => {
    setMode(newMode);
  };

  return (
    <Container style={styles}>
      <DiffHeader>
        <HeaderLeft>
          <IconFile fontSize={16} />
          <DiffTitle>{title || t('components:configuration.file')}</DiffTitle>
          <Tooltip content={t('components:line.by.line.comparison')}>
            <IconDown
              fontSize={16}
              style={{ cursor: 'pointer' }}
              onClick={handleModeChange('line-by-line')}
            />
          </Tooltip>
          <Tooltip content={t('components:side.by.side.comparison')}>
            <IconRight
              fontSize={16}
              style={{ cursor: 'pointer' }}
              onClick={handleModeChange('side-by-side')}
            />
          </Tooltip>
        </HeaderLeft>
        <HeaderRight>{description}</HeaderRight>
      </DiffHeader>
      <div dangerouslySetInnerHTML={{ __html: diffHtml }} />
    </Container>
  );
}

export const DiffYaml = React.memo(DiffYamlComponent, (prevProps, nextProps) => {
  return (
    isEqual(prevProps.datas, nextProps.datas) &&
    prevProps.title === nextProps.title &&
    prevProps.description === nextProps.description
  );
});

/** ============================ 样式 ==================================== */
const Container = styled.div`
  .d2h-file-header {
    display: none;
  }
  .d2h-files-diff {
    background-color: #fff;
  }
`;

const HeaderLayout = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DiffHeader = styled(HeaderLayout)`
  border: 1px solid ${borderColor};
  border-bottom: none;
  margin-bottom: -1px;
  padding: 0.5rem 1rem;
  justify-content: space-between;
`;

const HeaderLeft = styled(HeaderLayout)``;
const HeaderRight = styled(HeaderLayout)``;

const DiffTitle = styled.span``;
